import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  AlteracaoDePrecoPorItemContextData,
  AlteracaoDePrecoPorItemProviderProps,
  CachePesquisaDataProps,
  CachePesquisaInfoProps,
  warningProps,
} from '../types/context';
import { useForm } from 'react-hook-form';
import { LojaContext } from '~/context/loja';
import { alteracaoPrecoPorItem } from '../services';
import { PayloadStore, SearchProduto } from '../types';
import useAuth from '~/hooks/useAuth';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { nanoid } from 'nanoid';
import { GridRowData, GridRowParams } from '@material-ui/data-grid';
import { convertToDecimal } from '../utils/convertToDecimal';
import { toast } from 'react-toastify';
import { toleraValor } from '../utils/toleraValor';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import {
  formatCurrencyAsText,
  moneyFormat,
  transformAsCurrency,
} from '~/utils/functions';
import { useQueryClient } from 'react-query';
import { loadingContext } from '~/context/loading';
import { getWarningLabelFromProduct } from '../utils/getWarningLabelFromProduct';
import { calcMargemVenda, calcSugestaoVenda } from '../utils/calculos';

const MySwal = withReactContent(Swal);

export const AlteracaoDePrecoPorItemContext = createContext(
  {} as AlteracaoDePrecoPorItemContextData,
);

export const AlteracaoDePrecoPorItemProvider: React.FC<
  AlteracaoDePrecoPorItemProviderProps
> = ({ children }) => {
  const warningDefault = {
    type: '',
    message: '',
    has: false,
  };
  const { changeLoja, loja } = useContext(LojaContext);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [loader, setLoader] = useState(false);
  const [codProduto, setCodProduto] = useState('');
  const [productSelected, setProductSelected] = useState<SearchProduto | null>(
    null,
  );
  const [precoArredondamento, setPrecoArredondamento] = useState<any[]>([]);
  const [sugestaoVendaPorMargem, setsugestaoVendaPorMargem] = useState(0);
  const [sugestaoVendaPorCusto, setsugestaoVendaPorCusto] = useState(0);
  const [sugestaoVendaPorMargemFixa, setsugestaoVendaPorMargemFixa] =
    useState(0);
  const [sugestaoVendaPorCustoFixa, setsugestaoVendaPorCustoFixa] = useState(0);
  const [disableCancelButton, setDisableCancelButton] = useState(true);
  const [disableContent, setDisableContent] = useState(true);
  const [detalhesNfFornecedor, setDetalhesNfFornecedor] = useState<
    GridRowData[]
  >([]);
  const [showSearch, setShowSearch] = useState(true);
  const [isUpdate, setIsUpdate] = useState(false);
  const [warningLabel, setWarningLabel] =
    useState<warningProps>(warningDefault);

  const { setLoading } = useContext(loadingContext);

  const {
    register,
    control,
    formState,
    setValue,
    reset,
    getValues,
    handleSubmit,
    setFocus,
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    if (user) setPrecoArredondamento(user.precos_arredondamentos);
  }, [user]);

  useEffect(() => {
    setWarningLabel(getWarningLabelFromProduct(productSelected));
  }, [productSelected]);

  const onShowSearch = useCallback((show: boolean) => {
    setShowSearch(show);
  }, []);

  const formatDetalhesNfFornecedor = useCallback(
    (data: SearchProduto): GridRowData[] => {
      return data.detalhes_nf_fornecedor.map((dt) => {
        const dataEntrada = format(new Date(dt.dta_entrada), 'dd/MM/yyyy', {
          locale: ptBR,
        });

        return {
          id: nanoid(),
          apelido_fantasia: dt.apelido_fantasia,
          val_custo_rep: formatCurrencyAsText(dt.val_custo_rep),
          dta_entrada: dataEntrada,
          num_nf: dt.num_nf,
        };
      });
    },
    [],
  );

  const handleChangeProduct = useCallback(
    async (cod_produto: string, cod_loja: number) => {
      const productFound = await alteracaoPrecoPorItem.searchProduto(
        cod_produto,
        cod_loja,
      );

      setCodProduto(cod_produto);

      if (!productFound.success || !productFound.data) return;

      const { data } = productFound;
      const custoRep = convertToDecimal(data.val_custo_rep);
      const venda = convertToDecimal(data.val_venda);

      const calcMargVda = calcMargemVenda({ product: data, custoRep, venda });

      const sugVdaCusto = await calcSugestaoVenda({
        product: data,
        custoRep: data.val_custo_rep,
        margem: data.val_margem,
        precoArredondamento,
      });

      const sugVdaMargem = await calcSugestaoVenda({
        product: data,
        custoRep: data.val_custo_rep,
        margem: calcMargVda,
        precoArredondamento,
      });

      setsugestaoVendaPorCusto(sugVdaCusto);
      setsugestaoVendaPorMargem(sugVdaMargem);
      setsugestaoVendaPorCustoFixa(sugVdaCusto);
      setsugestaoVendaPorMargemFixa(sugVdaMargem);

      setDetalhesNfFornecedor(formatDetalhesNfFornecedor(data));
      setProductSelected(data);

      setValue('product_atual', data);
      setValue('val_custo_rep', formatCurrencyAsText(data.val_custo_rep));
      setValue('val_margem_calculada', moneyFormat(String(calcMargVda)));
      setValue('val_venda', formatCurrencyAsText(data.val_venda));
    },
    [formatDetalhesNfFornecedor, precoArredondamento, setValue],
  );

  const handleWarningLabel = useCallback((value: warningProps) => {
    setWarningLabel(value);
  }, []);

  const handleDisableCancelButton = useCallback((value: boolean) => {
    setDisableCancelButton(value);
  }, []);

  const handleDisableContent = useCallback((value: boolean) => {
    setDisableContent(value);
  }, []);

  const handlesugestaoVendaPorMargem = useCallback((value: number) => {
    setsugestaoVendaPorMargem(value);
  }, []);
  const handlesugestaoVendaPorCusto = useCallback((value: number) => {
    setsugestaoVendaPorCusto(value);
  }, []);

  const handleLimparCampos = useCallback(() => {
    setProductSelected(null);
    setCodProduto('');
    setsugestaoVendaPorMargem(0);
    setsugestaoVendaPorCusto(0);
    setsugestaoVendaPorMargemFixa(0);
    setsugestaoVendaPorCustoFixa(0);
    reset({
      busca_produto: '',
      val_custo_rep: '',
      val_margem_calculada: '',
      val_venda: '',
      product_atual: '',
    });
    setDisableCancelButton(true);
    setDisableContent(true);
    setDetalhesNfFornecedor([]);
  }, [reset]);

  const validateBloqueio = useCallback(
    async (params: {
      label: 'Custo' | 'Venda';
      percent: number;
      novoValor: number;
      valorAntigo: number;
    }): Promise<boolean> => {
      const { label, percent, novoValor, valorAntigo } = params;

      if (percent <= 0) return true;

      const tolera = toleraValor(novoValor, valorAntigo, percent);

      if (tolera) return true;

      if (label === 'Venda') setLoader(false); // mantém comportamento anterior

      await MySwal.fire({
        title: `${label} excedeu o percentual de bloqueio`,
        text: 'Regularize a situação para prosseguir',
        icon: 'error',
      });

      return false;
    },
    [],
  );

  const validateTolerancia = useCallback(
    async (params: {
      label: 'Custo' | 'Venda';
      percent: number;
      novoValor: number;
      valorAntigo: number;
    }): Promise<void> => {
      const { label, percent, novoValor, valorAntigo } = params;

      if (percent <= 0) return;

      const tolera = toleraValor(novoValor, valorAntigo, percent);

      if (tolera) return;

      await MySwal.fire({
        title: `${label} excedeu o percentual de tolerância`,
        icon: 'warning',
        confirmButtonText: 'Continuar',
      });
    },
    [],
  );

  const onSave = handleSubmit(async (data) => {
    const { product_atual } = getValues();

    try {
      setLoading(false);

      if (!product_atual)
        return toast.warning('Busque por um produto antes de gravar');

      const custoRep = transformAsCurrency(product_atual.val_custo_rep);
      const novoCustoRep = transformAsCurrency(data.val_custo_rep || 0);
      const valVenda = transformAsCurrency(product_atual.val_venda);
      const novoValVenda = transformAsCurrency(data.val_venda || 0);

      if (novoValVenda < novoCustoRep) {
        return toast.warning(
          'Preço de venda não pode ser menor que o preço de custo',
        );
      }

      const okBloqCusto = await validateBloqueio({
        label: 'Custo',
        percent: loja.regra_empresa.per_bloq_custo,
        novoValor: novoCustoRep,
        valorAntigo: custoRep,
      });
      if (!okBloqCusto) return;

      const okBloqVenda = await validateBloqueio({
        label: 'Venda',
        percent: loja.regra_empresa.per_bloq_venda,
        novoValor: novoValVenda,
        valorAntigo: valVenda,
      });
      if (!okBloqVenda) return;

      await validateTolerancia({
        label: 'Venda',
        percent: loja.regra_empresa.per_tol_venda,
        novoValor: novoValVenda,
        valorAntigo: valVenda,
      });

      await validateTolerancia({
        label: 'Custo',
        percent: loja.regra_empresa.per_tol_custo,
        novoValor: novoCustoRep,
        valorAntigo: custoRep,
      });

      setLoader(true);
      setLoading(true);

      const payload: PayloadStore = {
        item: product_atual,
        valores: {
          val_custo_rep: novoCustoRep,
          val_venda: novoValVenda,
        },
      };

      const response = await alteracaoPrecoPorItem.store(
        codProduto,
        loja.cod_loja,
        payload,
      );

      if (response.success) {
        const codigoAtual = product_atual.cod_produto || codProduto;

        handleLimparCampos();
        const nextItemStatus = await onNextItem(codigoAtual);

        const successMessage =
          nextItemStatus === 'finished-page'
            ? `${response.message} Você chegou ao final da página.`
            : response.message;

        toast.success(successMessage);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  });

  const onNew = useCallback(() => {
    handleLimparCampos();
    setShowSearch(false);
    setIsUpdate(false);
  }, [handleLimparCampos]);

  const onCancel = useCallback(() => {
    handleLimparCampos();
    setShowSearch(true);
    setIsUpdate(false);
  }, [handleLimparCampos]);

  const onRowClick = useCallback(
    async (param: GridRowParams) => {
      const { row } = param;
      const { cod_produto, cod_loja, des_produto } = row;

      await handleChangeProduct(cod_produto, cod_loja);

      setValue('busca_produto', { value: cod_produto, label: des_produto });
      setIsUpdate(true);
      setShowSearch(false);
      setDisableContent(false);
      setDisableCancelButton(false);

      setTimeout(() => {
        changeLoja(cod_loja);
        setFocus('val_custo_rep', { shouldSelect: true });
      }, 500);
    },
    [changeLoja, handleChangeProduct, setFocus, setValue],
  );

  const onNextItem = useCallback(
    async (
      currentCodProduto?: string,
    ): Promise<'next-item' | 'finished-page'> => {
      const codigoAtual = currentCodProduto || codProduto;

      const cachePesquisaInfo =
        queryClient.getQueryData<CachePesquisaInfoProps>('cacheLastSearch_290');

      if (!cachePesquisaInfo) {
        onCancel();
        return 'finished-page';
      }

      const cachePesquisaData =
        queryClient.getQueryData<CachePesquisaDataProps>([
          'cache_tela_290',
          Number(cachePesquisaInfo.pag),
          Number(cachePesquisaInfo.limite),
        ]);

      if (!cachePesquisaData?.data?.length) {
        onCancel();
        return 'finished-page';
      }

      const currentItemIdx = cachePesquisaData.data.findIndex(
        (item) => String(item.cod_produto) === String(codigoAtual),
      );

      if (currentItemIdx === -1) {
        onCancel();
        return 'finished-page';
      }

      const nextItem = cachePesquisaData.data[currentItemIdx + 1];

      if (nextItem) {
        await onRowClick({ row: nextItem } as any);
        return 'next-item';
      }

      onCancel();
      return 'finished-page';
    },
    [codProduto, onCancel, onRowClick, queryClient],
  );

  return (
    <AlteracaoDePrecoPorItemContext.Provider
      value={{
        loader,
        productSelected,
        codProduto,
        sugestaoVendaPorMargem,
        sugestaoVendaPorCusto,
        sugestaoVendaPorCustoFixa,
        sugestaoVendaPorMargemFixa,
        disableCancelButton,
        detalhesNfFornecedor,
        disableContent,
        precoArredondamento,
        showSearch,
        isUpdate,
        warningLabel,
        handleWarningLabel,

        register,
        control,
        formState,
        setValue,
        setFocus,
        getValues,

        changeLoja,
        handleChangeProduct,
        handleLimparCampos,
        handleDisableCancelButton,
        handleDisableContent,
        handlesugestaoVendaPorMargem,
        handlesugestaoVendaPorCusto,
        onSave,
        onShowSearch,
        onNew,
        onCancel,
        onRowClick,
      }}
    >
      {children}
    </AlteracaoDePrecoPorItemContext.Provider>
  );
};
